/** * 新規メッセージ送信コンポーネント */
<template>
  <div class="message-post">
    <!-- フォームグループ(本体) -->
    <form-group
      id="message-form"
      ref="formGroup"
      v-bind:onIfieldApiError="handleApiError"
      v-on:form-group-value-change="$_onFormGroupValueChange"
    >
      <template
        v-slot:[FIELD_NAMES.CONTENT]="{
          idPrefix,
          value,
          disabled,
          placeholder,
          state,
          setValue,
        }"
      >
        <input-text
          v-bind:id-prefix="idPrefix"
          v-bind:value="value"
          v-bind:templates="$store.getters['textTemplate/sortedTextTemplates']"
          v-bind:disabled="disabled"
          v-bind:state="state"
          v-bind:placeholder="placeholder"
          trim-end
          v-on:changed="setValue"
          v-on:text-template-used="
            $store.dispatch('textTemplate/moveFirst', $event.id)
          "
        />
      </template>
      <template v-slot:[FIELD_NAMES.SCHEDULE_ID]="{ value, setValue }">
        <schedule-selector
          ref="schedule-selector"
          v-bind:formSize="'md'"
          v-bind:selectType="0"
          v-bind:selectedAllItemLabel="''"
          v-bind:defaultIds="value ? [value] : []"
          v-bind:defaultSearchParams="scheduleSearchParams"
          v-on:schedule-selectend="
            (x) => setValue(x.length === 0 ? null : x[0].id)
          "
        />
      </template>
    </form-group>
    <loading-mask isFullScreen v-bind:isLoading="isLoading" />
  </div>
</template>

<script>
import InputText from '@/components/AppInputText.vue'
import ScheduleSelector from '@/components/schedule/AppScheduleSelector.vue'
import Schedule from '@/model/Schedule.js'
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { RestApiClient } from 'ifield-core-components'
import { mapGetters } from 'vuex'
import { toServerTz } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import {
  FUNCTION_TYPE,
  DATE_FORMAT_HYPHEN,
  TIME_FORMAT_MILLISECOND,
} from '@/constants.js'
import moment from 'moment'

// 日時フォーマット(ミリ秒)
const DATETIME_FORMAT_MILLISECOND = `${DATE_FORMAT_HYPHEN}T${TIME_FORMAT_MILLISECOND}`

/**
 * 項目名一覧(値はsend_messageのリクエストパラメータ)
 */
const FIELD_NAMES = {
  /** 宛先ユーザー */
  RECEIVE_USERS: 'receive_users',
  /** 宛先組織 */
  RECEIVE_ORGANIZATIONS: 'receive_organizations',
  /** メッセージ内容 */
  CONTENT: 'content',
  /** スケジュール */
  SCHEDULE_ID: 'schedule_id',
  /** 添付ファイル */
  ATTACH_FILES: 'temp_attach_files',
}
/**
 * 内部利用する宛先のフィールド名
 */
const _FIELD_RECEIVE_TARGETS = '_receive_targets'

export default {
  name: 'AppMessagePost',
  components: {
    'input-text': InputText,
    'schedule-selector': ScheduleSelector,
  },
  props: {
    /**
     * デフォルトで選択されている宛先ユーザーID一覧
     */
    defaultReceiveUsers: {
      type: Array,
      default: () => [],
      required: false,
    },
    /**
     * 引用文
     */
    replyContent: {
      type: String,
      default: null,
      required: false,
    },
  },
  setup(props, context) {
    const { handleApiError, showApiError } = useHandleApiError(props, context)
    return { handleApiError, showApiError, FIELD_NAMES }
  },
  data: function () {
    return {
      /**
       * フォームの値
       * @type {Object}
       */
      formValues: {},
      /**
       * ロード中フラグ
       * @type {Boolean}
       */
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters('userKeywordTarget', ['keywordTargets']),
    /**
     * ログインユーザーが管理者の場合はtrue
     * @type {Boolean}
     */
    isManager: function () {
      return this.$store.state.login.isManager
    },
    /**
     * スケジュール機能が利用可能か？
     * @type {Boolean}
     */
    isScheduleAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.SCHEDULE)
    },
    /**
     * 項目ラベルMap(key: 項目名(API名), value: 表示ラベル)
     * @return {Map}
     */
    fieldLabelMap: function () {
      return new Map([
        [
          FIELD_NAMES.RECEIVE_USERS,
          this.$t('appMessagePost.field.receiveTargets'),
        ],
        [
          FIELD_NAMES.RECEIVE_ORGANIZATIONS,
          this.$t('appMessagePost.field.receiveTargets'),
        ],
        [FIELD_NAMES.CONTENT, this.$t('appMessagePost.field.message')],
        [FIELD_NAMES.SCHEDULE_ID, this.$t('appMessagePost.field.schedule')],
        [FIELD_NAMES.ATTACH_FILES, this.$t('appMessagePost.field.attachFiles')],
      ])
    },
    /**
     * 宛先ユーザー値(結合)
     * @type {Object|null}
     */
    receiveTargetValue: function () {
      const value = {
        user: this.formValues[FIELD_NAMES.RECEIVE_USERS],
        organization: this.formValues[FIELD_NAMES.RECEIVE_ORGANIZATIONS],
      }
      if (Object.values(value).every((v) => !v?.length)) return null
      return value
    },
    /**
     * AppFormGroupに設定するデータ
     * @type {Array<Object>}
     */
    formData: function () {
      const forms = [
        /** 宛先 */
        {
          fieldName: _FIELD_RECEIVE_TARGETS,
          label: this.$t('appMessagePost.field.receiveTargets'),
          attributeType: ATTRIBUTE_TYPE.MULTI_USER,
          value: this.receiveTargetValue,
          disabled: this.isReceivedUsersDisabled,
          validateRule: {
            required: true,
          },
          targetUserType: 'app',
          allOrganizations: true,
          primaryOrganizationId:
            this.$store.getters['userProfile/defaultOrganizationId'],
          indoorSites: this.$store.getters['indoorSite/indoorSites'],
          keywordTarget: this.keywordTargets,
          onKeywordTargetChange: (data) => {
            this.$store.dispatch('userKeywordTarget/setKeywordTargets', data)
          },
        },
        /** メッセージ内容 */
        {
          fieldName: FIELD_NAMES.CONTENT,
          label: this.fieldLabelMap.get(FIELD_NAMES.CONTENT),
          attributeType: ATTRIBUTE_TYPE.SLOT,
          value: this.formValues[FIELD_NAMES.CONTENT],
          placeholder: this.$t('appMessagePost.placeholder.content'),
          validateRule: {
            required: true,
          },
        },
        /** 添付ファイル */
        {
          fieldName: FIELD_NAMES.ATTACH_FILES,
          label: this.fieldLabelMap.get(FIELD_NAMES.ATTACH_FILES),
          attributeType: ATTRIBUTE_TYPE.FILE,
          value: this.formValues[FIELD_NAMES.ATTACH_FILES],
          multiple: true,
          accept:
            'image/png,image/jpeg,image/gif,image/tiff,application/pdf,video/mp4',
          acceptLabel: 'png, jpg, gif, tiff, pdf, mp4',
        },
      ]

      if (this.isManager && this.isScheduleAvailable) {
        /** スケジュール */
        forms.splice(2, 0, {
          fieldName: FIELD_NAMES.SCHEDULE_ID,
          label: this.fieldLabelMap.get(FIELD_NAMES.SCHEDULE_ID),
          attributeType: ATTRIBUTE_TYPE.SLOT,
          value: this.formValues[FIELD_NAMES.SCHEDULE_ID],
        })
      }

      return forms
    },
    /**
     * メッセージ本文のデフォルト値
     */
    defaultContent: function () {
      if (!this.replyContent) {
        return null
      }
      return (
        this.replyContent
          .split('\n')
          .map((line) => `> ${line}`)
          .join('\n') + '\n'
      )
    },
    /**
     * 宛先フォームが非活性の場合はtrue
     */
    isReceivedUsersDisabled: function () {
      return !!this.formValues[FIELD_NAMES.SCHEDULE_ID]
    },
    /**
     * スケジュール選択の検索条件
     * @type {Object}
     */
    scheduleSearchParams: function () {
      if (!this.receiveTargetValue) {
        return { userId: null, organizationId: null }
      }
      const { user, organization } = this.receiveTargetValue
      return { userId: user, organizationId: organization }
    },
  },
  watch: {
    keywordTargets: function () {
      // 値を保持したままフォームを再セットする
      this.$_getFormValues(true)
      this.form.setData(this.formData)
    },
  },
  created: function () {
    this.$_updateIndoorSites()
    this.$_initializeFormValues()
  },
  mounted: function () {
    this.form = this.$refs['formGroup']
    this.$log.info('AppMessagePost mounted.')
  },
  methods: {
    /**
     * サイトデータ更新
     */
    $_updateIndoorSites: async function () {
      try {
        this.$store.dispatch('indoorSite/updateIndoorSitesWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.info('failed to fetch indoor sites. use fetched before.')
        })
      }
    },

    /**
     * フォーム値を初期化する
     */
    $_initializeFormValues: function () {
      const values = {}
      for (const fieldName of Object.values(FIELD_NAMES)) {
        let value = null
        if (fieldName === FIELD_NAMES.RECEIVE_USERS) {
          value =
            this.defaultReceiveUsers.length === 0
              ? null
              : [...this.defaultReceiveUsers]
        } else if (fieldName === FIELD_NAMES.RECEIVE_ORGANIZATIONS) {
          value = []
        } else if (fieldName == FIELD_NAMES.CONTENT) {
          value = this.defaultContent
        }
        values[fieldName] = value
      }
      this.formValues = values
    },

    /**
     * フォームを表示する
     * @public
     */
    show: async function () {
      this.$_updateTextTemplates()
      this.form.setData(this.formData)
    },

    /**
     * 文字列テンプレートを更新する
     */
    $_updateTextTemplates: async function () {
      try {
        await this.$store.dispatch(
          'textTemplate/updateTextTemplatesWithThrottle'
        )
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.info('failed to fetch text templates. use fetched before.')
        )
      }
    },

    /**
     * フォームをクリアする
     */
    clear: function () {
      this.$_initializeFormValues()
      this.form.setData([])
    },

    /**
     * フォームグループの値変更時の処理
     *
     * @param {String} fieldName フィールド名
     * @param {*} value 変更後の値
     */
    $_onFormGroupValueChange: function (fieldName, value) {
      // 承認権限が無い場合は何もしない
      if (!this.isManager) return
      if (fieldName === _FIELD_RECEIVE_TARGETS) {
        // 変更が宛先の場合
        this.$_onChangeReceiveTargets(value)
      } else if (fieldName === FIELD_NAMES.SCHEDULE_ID) {
        // 変更がスケジュールの場合
        this.$_onChangeScheduleId(value)
      }
    },

    /**
     * 宛先変更時の処理
     * @param {Object} value 変更後の値
     */
    $_onChangeReceiveTargets: function (value) {
      const values = {}
      const { user, organization } = value || {
        user: [],
        organization: [],
      }
      values[FIELD_NAMES.RECEIVE_USERS] = user
      values[FIELD_NAMES.RECEIVE_ORGANIZATIONS] = organization
      this.$_updateFormValues(values)
    },

    /**
     * スケジュール変更時の処理
     *
     * @param {Number} id スケジュールID
     */
    $_onChangeScheduleId: async function (id) {
      const values = {}
      if (id === null) {
        // スケジュールを空に更新
        values[FIELD_NAMES.SCHEDULE_ID] = null
      } else {
        // フォームの値を強制取得
        this.$_getFormValues(true)
        // 宛先フォームにスケジュールの全担当者を設定
        try {
          await this.$_getSchedule(id)
          values[FIELD_NAMES.RECEIVE_USERS] = this.schedule.targetUserIds
          values[FIELD_NAMES.RECEIVE_ORGANIZATIONS] =
            this.schedule.targetOrganizationIds
        } catch {
          // 宛先は空にする
          values[FIELD_NAMES.RECEIVE_USERS] = []
          values[FIELD_NAMES.RECEIVE_ORGANIZATIONS] = []
        }
      }
      this.$_updateFormValues(values)
      // フォーム再セット
      this.form.setData(this.formData)
    },

    /**
     * フォームの値を取得する
     *
     * @param {Boolean} force 強制取得する場合はtrue
     */
    $_getFormValues: function (force) {
      const values = this.form.getValues(force)
      const newValues = {}
      for (const item of values) {
        if (item.name === _FIELD_RECEIVE_TARGETS) {
          const { user, organization } = item.value || {
            user: [],
            organization: [],
          }
          newValues[FIELD_NAMES.RECEIVE_USERS] = user
          newValues[FIELD_NAMES.RECEIVE_ORGANIZATIONS] = organization
        } else {
          newValues[item.name] = item.value
        }
      }
      this.$_updateFormValues(newValues)
    },

    /**
     * フォームの値をリアクティブに更新する
     *
     * @param {Object} newValues 更新する値のkey-valueペア
     */
    $_updateFormValues: function (newValues) {
      const values = { ...this.formValues }
      for (const [key, value] of Object.entries(newValues)) {
        values[key] = value
      }
      this.formValues = values
    },

    /**
     * スケジュールを取得する
     *
     * @param {Number} id 対象ID
     */
    $_getSchedule: async function (id) {
      const url = RestApiClient.createApiUrl(`schedule/${id}`)
      try {
        const json = await RestApiClient.executeGetApi(url)
        // 対象ユーザー・組織を取得するだけなので日時のタイムゾーン変換は行わない
        this.schedule = new Schedule(json.data)
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * カレントの宛先を取得する
     * 宛先フォームが非活性の時はfalseを返す
     * @returns {Array.<Number>|Boolean} ユーザーID配列
     * @public
     */
    getCurrentReceiveUsers: function () {
      return this.isReceivedUsersDisabled ? false : this.receiveTargetValue
    },

    /**
     * カレントの宛先を設定する
     * @param {Object} value 宛先データ
     * @public
     */
    setCurrentReceiveUsers: function (value) {
      if (!this.isReceivedUsersDisabled) {
        this.form.setValue(_FIELD_RECEIVE_TARGETS, value)
      }
    },

    /**
     * メッセージを送信する
     * @public
     */
    sendMessage: async function () {
      this.$_showLoading()

      // 保存用のメッセージを作成
      let newMessage = this.$_createMessageFromForm()
      if (!newMessage) {
        // 値が取得できなかった場合は、ここで終了
        this.$_hideLoading()
        return
      }

      let url = RestApiClient.createApiUrl('send_message')
      try {
        await RestApiClient.executePostApi(url, newMessage)
        this.$_onSendSuccess()
      } catch (error) {
        this.handleApiError(error, this.$_onSaveFailure)
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * フォームデータから保存用のメッセージデータを作成する
     * @return {object} メッセージ、ただし項目に不正な値が設定されている場合はnullを返却する
     */
    $_createMessageFromForm: function () {
      // フォームグループの値を取得する
      try {
        this.$_getFormValues()
      } catch {
        return
      }

      let newMessage = {
        'send_at': toServerTz(+moment(), DATETIME_FORMAT_MILLISECOND),
      }

      for (const [fieldName, value] of Object.entries(this.formValues)) {
        if (
          [
            FIELD_NAMES.RECEIVE_USERS,
            FIELD_NAMES.RECEIVE_ORGANIZATIONS,
          ].includes(fieldName)
        ) {
          newMessage[fieldName] = value.map((id) => ({ id }))
        } else if (fieldName === FIELD_NAMES.CONTENT) {
          newMessage[fieldName] =
            typeof value === 'string' ? value.trimEnd() : value
        } else if (fieldName === FIELD_NAMES.ATTACH_FILES) {
          const { tempFiles } = value || {}
          if (tempFiles && tempFiles.length) {
            newMessage[fieldName] = tempFiles.map((file) => ({
              temp_file_path: file.tempFilePath,
            }))
          }
        } else if (fieldName === FIELD_NAMES.SCHEDULE_ID) {
          newMessage[fieldName] = value
        }
      }

      return newMessage
    },

    /**
     * 保存処理が成功した時の処理
     */
    $_onSendSuccess: function () {
      this.$_showSentToast()
      /**
       * メッセージ送信完了イベント
       */
      this.$emit('message-post-end')
    },

    /**
     * 送信完了トーストを表示する
     */
    $_showSentToast: function () {
      this.$toast.fire({
        position: 'top',
        icon: 'success',
        title: this.$t('appMessagePost.message.sentMessage'),
        showConfirmButton: true,
        confirmButtonColor: '#6c757d',
        timer: 2500,
      })
    },

    /**
     * 保存失敗時の処理
     * @param {Error} error エラー
     */
    $_onSaveFailure: async function (error) {
      if (!(error instanceof Response)) {
        this.showApiError(error)
        return
      }

      const json = await error.json()
      if (json.errors) {
        // 項目ごとにエラーを表示する
        const messages = []
        for (const error of json.errors) {
          const fieldLabel = this.$_getLabel(error.field)
          if (fieldLabel) {
            messages.push(
              this.$t('appMessagePost.message.invalidField', {
                name: fieldLabel,
              })
            )
          } else {
            messages.push(error.message)
          }
          if (error.field === FIELD_NAMES.SCHEDULE_ID) {
            // スケジュールの選択を解除する
            const values = {}
            values[FIELD_NAMES.SCHEDULE_ID] = null
            this.$_updateFormValues(values)
            this.$refs['schedule-selector'].setValue(null)
          }
        }
        this.$_showFailedToSendAlert(messages)
      } else {
        // 項目ごとのエラーが設定されていない場合は、エラーメッセージを表示する
        this.$_showFailedToSendAlert([], json.message)
      }
    },

    /**
     * 指定された項目名のラベルを取得する
     * @param {string} 取得対象の項目名(API名)
     * @return {string} 表示ラベル
     */
    $_getLabel(fieldName) {
      return this.fieldLabelMap.get(fieldName)
    },

    /**
     * 送信失敗アラートを表示する
     *
     * @param {Array.<String>} messages エラーメッセージ
     */
    $_showFailedToSendAlert: function (messages, title) {
      return this.$alert.fire({
        icon: 'error',
        title: title || this.$t('appMessagePost.message.failedToSend'),
        html: messages.join('<br/>'),
      })
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>
